import {
  IWidgetComponentProps,
  SelectWidgetWrapper,
  getWidgetProps,
} from '@framework/engine';
import { SelectInput, useField } from '@framework/form';
import { RowElement } from '@framework/ui';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { IProvinceWidget } from '../IProvinceWidget';
import {
  getProvinceDataFieldName,
  getProvinceFieldName,
  getTownDataFieldName,
  getTownFieldName,
} from '../createProvinceWidgetSchema';
import { IProvinceItem, ITownItem } from '../utils/mapProvinceList';

export interface IProvinceWidgetSelectComponentProps
  extends IWidgetComponentProps<IProvinceWidget> {
  provinces?: IProvinceItem[];
  towns?: ITownItem[];
  loading?: boolean;
}

/**
 * Componente generale che gestisce le select di provincia e comune.
 * Utilizzato "senza parametri" come preview del widget.
 */
export function ProvinceWidgetSelectComponent(
  props: IProvinceWidgetSelectComponentProps,
) {
  const { widget, loading, provinces, towns } = props;
  const widgetOptions = getWidgetProps(props);

  const placeholders = useMemo(() => {
    if (props.componentLayout?.hidePlaceholder) {
      return { town: '', province: '' };
    }
    const { townPlaceholder, provincePlaceholder } = widget;

    return { town: townPlaceholder ?? '', province: provincePlaceholder ?? '' };
  }, [props.componentLayout?.hidePlaceholder, widget]);

  const provinceFieldName = getProvinceFieldName(widgetOptions.name);
  const provinceDataFieldName = getProvinceDataFieldName(widgetOptions.name);
  const townFieldName = getTownFieldName(widgetOptions.name);
  const townDataFieldName = getTownDataFieldName(widgetOptions.name);

  const provinceDataField = useField(provinceDataFieldName);
  const selectedProvince = useWatch({
    name: provinceFieldName,
  });

  const townDataField = useField(townDataFieldName);

  // Mostro solo i comuni della provincia selezionata
  const filteredTowns = useMemo(() => {
    if (!selectedProvince || !towns) return [];
    return towns.filter(town => town.context?.provincia === selectedProvince);
  }, [selectedProvince, towns]);

  return (
    <RowElement>
      <SelectWidgetWrapper
        {...props}
        name={provinceFieldName}
        formItemName={provinceFieldName}
        label={widget.provinceLabel}
        helpText={widget.provinceHelpText}
      >
        <SelectInput
          name={provinceFieldName}
          readOnly={props.readonly}
          options={provinces ?? []}
          loading={loading}
          optionFilterProp="label"
          allowClear
          placeholder={placeholders.province}
          // per accessibilità limitiamo virtual a 20 opzioni
          showSearch={true}
          onChange={(value, option: any, onChange) => {
            provinceDataField.setValue(option?.context ?? null);
            onChange(value);
          }}
        />
      </SelectWidgetWrapper>
      <SelectWidgetWrapper
        {...props}
        name={townFieldName}
        formItemName={townFieldName}
        label={widget.townLabel}
        helpText={widget.townHelpText}
      >
        <SelectInput
          disabled={!selectedProvince && !props.preview}
          name={townFieldName}
          readOnly={props.readonly}
          options={filteredTowns ?? []}
          loading={loading}
          optionFilterProp="label"
          allowClear
          placeholder={placeholders.town}
          // per accessibilità limitiamo virtual a 20 opzioni
          onChange={(value, option: any, onChange) => {
            townDataField.setValue(option?.context ?? null);
            onChange(value);
          }}
          showSearch={true}
        />
      </SelectWidgetWrapper>
    </RowElement>
  );
}
